import { useTranslation } from 'react-i18next';
import { formatCurrency } from '@/lib/utils';
import type { PortfolioTotals } from '../types';

/**
 * Recurring-revenue portfolio banner (S11): a small stat row above the subscriptions
 * list showing how many subscriptions are in view, their total recurring value (per
 * currency), and how many are expiring soon. Pure presentation — the totals are
 * computed by computePortfolioTotals from the rows already loaded (no endpoint).
 */
export default function PortfolioBanner({ totals }: { totals: PortfolioTotals }) {
  const { t } = useTranslation('subscriptions');
  const currencies = Object.entries(totals.byCurrency);

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="subscriptions-portfolio">
      <div className="rounded-lg border bg-card px-4 py-3">
        <p className="text-xs text-muted-foreground">{t('portfolio.subscriptions', 'Subscriptions')}</p>
        <p className="text-lg font-semibold tabular-nums" data-testid="portfolio-count">{totals.count}</p>
      </div>
      <div className="rounded-lg border bg-card px-4 py-3">
        <p className="text-xs text-muted-foreground">{t('portfolio.recurringValue', 'Total contract value')}</p>
        <p className="text-lg font-semibold tabular-nums" data-testid="portfolio-recurring-value">
          {currencies.length === 0
            ? '—'
            : currencies.map(([cur, sum]) => formatCurrency(sum, cur)).join(' · ')}
        </p>
      </div>
      <div className="rounded-lg border bg-card px-4 py-3">
        <p className="text-xs text-muted-foreground">{t('portfolio.expiringSoon', 'Expiring within 90 days')}</p>
        <p className="text-lg font-semibold tabular-nums" data-testid="portfolio-expiring">{totals.expiringSoon}</p>
      </div>
    </div>
  );
}
